import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ChefHat, Bike, PackageCheck, Headphones, MapPin, Phone, XCircle } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { NotificationBell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STEPS = [
  { key: "pending", label: "Pedido recebido", icon: Check },
  { key: "preparing", label: "Preparando", icon: ChefHat },
  { key: "picked_up", label: "A caminho", icon: Bike },
  { key: "delivered", label: "Entregue", icon: PackageCheck },
];
const ORDER = ["pending", "accepted", "preparing", "ready", "picked_up", "delivered"];

export default function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [chat, setChat] = useState(false);

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id]);

  if (!order) return <div className="p-6 text-sm text-gray-500">Carregando pedido…</div>;

  const idx = ORDER.indexOf(order.status);
  const stepReached = (k) => idx >= ORDER.indexOf(k);

  return (
    <div data-testid="order-tracking-page" className="pb-28">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/pedidos")} className="p-1 gf-press" data-testid="tracking-back-button">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-display font-extrabold text-lg leading-none">Pedido {order.code}</h1>
          <p className="text-[11px] text-gray-500">{order.store_name}</p>
        </div>
        <NotificationBell />
      </header>

      <div className="px-4 pt-5">
        <div className="rounded-3xl gf-gradient text-white p-5" data-testid="order-status-card">
          <p className="text-[11px] uppercase tracking-wider font-bold text-white/80">Status atual</p>
          <h2 className="font-display text-2xl font-extrabold mt-1" data-testid="order-status-label">
            {order.status === "rejected" ? "Recusado pela loja" :
              order.status === "delivered" ? "Entregue — bom apetite!" :
              order.status === "picked_up" ? "A caminho de você" :
              order.status === "ready" ? "Pronto, aguardando motoboy" :
              order.status === "preparing" ? "Preparando seu pedido" :
              order.status === "accepted" ? "Pedido aceito pela loja" : "Pedido recebido"}
          </h2>
          <p className="text-xs text-white/85 mt-1">Previsão de entrega em {order.distance_km} km · {order.address}</p>
        </div>
      </div>

      <div className="px-4 mt-6">
        {order.delivery_code && order.status !== "delivered" && (
          <div className="rounded-2xl border-2 border-dashed border-gf-orange bg-orange-50 p-4 mb-5 text-center"
            data-testid="delivery-code-card">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Código de confirmação da entrega
            </p>
            <p className="font-display text-4xl font-extrabold tracking-[0.3em] text-gf-orange mt-1"
              data-testid="delivery-code">{order.delivery_code}</p>
            <p className="text-[11px] text-gray-600 mt-1">
              São os 4 últimos dígitos do seu telefone. Informe ao motoboy só quando receber o pedido em mãos.
            </p>
          </div>
        )}
        <div className="relative pl-1" data-testid="order-timeline">
          {STEPS.map((s, i) => {
            const done = stepReached(s.key);
            return (
              <div key={s.key} className="flex gap-3 pb-6 last:pb-0 relative">
                {i < STEPS.length - 1 && (
                  <span
                    className="absolute left-[17px] top-9 bottom-0 w-[2px]"
                    style={{ background: done ? "#FF7A1A" : "#E5E7EB" }}
                  />
                )}
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10"
                  style={{ background: done ? "#FF7A1A" : "#F3F4F6", color: done ? "#fff" : "#9CA3AF" }}
                >
                  <s.icon size={17} />
                </span>
                <div className="pt-1.5">
                  <p className={`text-sm font-bold ${done ? "text-gf-black" : "text-gray-400"}`}>{s.label}</p>
                  {done && order.timeline?.find((t) => t.status === s.key) && (
                    <p className="text-[11px] text-gray-500">
                      {new Date(order.timeline.find((t) => t.status === s.key).at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {order.courier_name && (
        <div className="px-4">
          <div className="rounded-2xl border border-gray-200 p-4 flex items-center gap-3" data-testid="courier-card">
            <img
              src="https://images.pexels.com/photos/31635924/pexels-photo-31635924.jpeg?auto=compress&cs=tinysrgb&w=200"
              alt="Motoboy"
              className="w-12 h-12 rounded-full object-cover"
            />
            <div className="flex-1">
              <p className="font-display font-bold text-sm">{order.courier_name}</p>
              <p className="text-[11px] text-gray-500 flex items-center gap-1">
                <MapPin size={11} /> A {order.distance_km} km de você · Magé/RJ
              </p>
            </div>
            <a href="tel:+5521999990000" className="p-2.5 rounded-full bg-mage-green/10 text-mage-green gf-press">
              <Phone size={17} />
            </a>
          </div>
        </div>
      )}

      <div className="px-4 mt-5">
        <h3 className="font-display font-bold text-base">Itens</h3>
        <div className="mt-2 rounded-2xl border border-gray-200 p-4 text-sm space-y-1.5">
          {order.items.map((i, k) => (
            <div key={k} className="flex justify-between text-gray-600">
              <span>{i.qty}× {i.name}</span><span>{brl(i.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between font-display font-extrabold pt-2 border-t border-dashed">
            <span>Total</span><span className="text-gf-orange" data-testid="order-total">{brl(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="px-4 mt-5">
        {["pending", "accepted", "preparing", "ready"].includes(order.status) && (
          <Button
            data-testid="cancel-order-button"
            variant="outline"
            onClick={async () => {
              const reason = window.prompt("Motivo do cancelamento (opcional):") ?? "";
              try {
                await api.post(`/orders/${order.id}/cancel`, { reason });
                toast.success("Pedido cancelado");
              } catch (e) {
                toast.error(apiError(e.response?.data?.detail));
              }
            }}
            className="w-full h-12 rounded-2xl font-bold border-mage-red text-mage-red mb-3"
          >
            <XCircle size={17} className="mr-2" /> Cancelar pedido
          </Button>
        )}
        {order.status === "cancelled" && (
          <div data-testid="cancelled-banner" className="rounded-2xl border-2 border-mage-red bg-red-50 p-4 mb-3">
            <p className="text-sm font-bold text-mage-red">Pedido cancelado</p>
            <p className="text-xs text-gray-600">{order.cancel_reason}</p>
          </div>
        )}
        <Button
          data-testid="support-chat-button"
          variant="outline"
          onClick={() => setChat((c) => !c)}
          className="w-full h-12 rounded-2xl font-bold border-gf-orange text-gf-orange"
        >
          <Headphones size={17} className="mr-2" /> Falar com o suporte
        </Button>
        {chat && (
          <div data-testid="support-chat-panel" className="mt-3 rounded-2xl bg-gf-gray p-4 text-sm">
            <p className="font-semibold">Suporte Magé Time Food</p>
            <p className="text-gray-600 mt-1 text-xs">
              Olá! Estamos acompanhando seu pedido {order.code}. Como podemos ajudar? Atendimento das 9h às 23h
              pelo WhatsApp (21) 99999-0000.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
